import {Cell} from 'notebook/tables/constants/CellConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
/**
 * Computes whether fill-down is enabled for the table as a feature.
 * Fill-down is enabled when `enableFillDown` is explicitly true,
 * or when formulas are not disabled (fill-down is a formula-adjacent feature).
 *
 * @param enableFillDown - The explicit enableFillDown prop value
 * @param disableFormulas - Whether formulas are disabled for this table
 */
export function computeIsFillDownEnabled(
  enableFillDown: boolean | undefined,
  disableFormulas: boolean
): boolean {
  return enableFillDown === true || !disableFormulas;
}

/**
 * Computes whether fill-down is currently active (user is dragging the fill handle).
 * Reads from the multiExtra state and defaults to false when not set.
 *
 * @param fillDownActive - The fillDownActive state value from multiExtra
 */
export function computeIsFillDownActive(fillDownActive: boolean | undefined): boolean {
  return fillDownActive ?? false;
}

/**
 * Computes whether the current fill-down operation is invalid (e.g. dragging
 * over merged regions). Reads from the multiExtra state and defaults to false.
 *
 * @param fillDownInvalid - The fillDownInvalid state value from multiExtra
 */
export function computeIsFillDownInvalid(fillDownInvalid: boolean | undefined): boolean {
  return fillDownInvalid ?? false;
}

/**
 * Extracts the fill-down box from the multiExtra state.
 * The fill-down box represents the range of cells being filled.
 *
 * @param fillDownBox - The fillDownBox state value from multiExtra
 */
export function computeFillDownBox(
  fillDownBox: MultiSelectionBox | null | undefined
): MultiSelectionBox | null | undefined {
  return fillDownBox;
}

/**
 * Computes the renderable fill-down box by subtracting the portion that overlaps
 * with the current selection. Returns null when the fill-down box is fully
 * contained within the selection (nothing to render) or when inputs are missing.
 *
 * Handles both horizontal fills (same rows, extended columns) and vertical fills
 * (same columns, extended rows).
 *
 * @param fillDownBox - The raw fill-down box from state
 * @param expandedBox - The expanded selection box representing the current selection
 */
export function computeFillDownRenderBox(
  fillDownBox: MultiSelectionBox | null | undefined,
  expandedBox: MultiSelectionBox | null
): MultiSelectionBox | null {
  if (fillDownBox == null || expandedBox == null) {
    return null;
  }

  let {minI, maxI, minJ, maxJ} = fillDownBox;
  const {minI: sbMinI, maxI: sbMaxI, minJ: sbMinJ, maxJ: sbMaxJ} = expandedBox;

  // If the fill-down region is completely contained within the selection, don't render.
  if (sbMinI <= minI && minI <= maxI && maxI <= sbMaxI && sbMinJ <= minJ && minJ <= maxJ && maxJ <= sbMaxJ) {
    return null;
  }

  // Otherwise, subtract out the region that is in the selection.
  if (minI === sbMinI && maxI === sbMaxI) {
    // Horizontal fill
    if (maxJ > sbMaxJ) {
      minJ = sbMaxJ + 1;
    } else if (minJ < sbMinJ) {
      maxJ = sbMinJ - 1;
    }
  } else if (maxI > sbMaxI) {
    // Vertical fill extending down
    minI = sbMaxI + 1;
  } else if (minI < sbMinI) {
    // Vertical fill extending up
    maxI = sbMinI - 1;
  }

  return {minI, maxI, minJ, maxJ};
}

/**
 * Returns the index of the last consecutive blank, non-merged row in the table
 * following the selection. Scans downward from the row after the selection's maxI,
 * stopping when any cell in the selected column range contains text, a non-empty
 * typed value, or belongs to a merged region.
 */
export function computeMaxRowOnFillDown(
  tableCells: Array<Array<Cell>>,
  computedTable: Array<Array<EvaluatedCell>>,
  expandedBox: MultiSelectionBox
): number {
  const selectionStartRowIndex = expandedBox.maxI + 1;
  const selectionStartColIndex = expandedBox.minJ;
  const selectionEndColIndex = expandedBox.maxJ;
  let fillDownEndRowIndex = tableCells.length - 1;
  for (let rowIndex = selectionStartRowIndex; rowIndex < tableCells.length; rowIndex++) {
    for (let colIndex = selectionStartColIndex; colIndex <= selectionEndColIndex; colIndex++) {
      const cell = tableCells[rowIndex]?.[colIndex];
      const computedCell = computedTable[rowIndex]?.[colIndex];
      if (
        (cell && (cell.text || (cell.typedValue && !isTypedValueEmpty(cell.typedValue)))) ||
        computedCell?.mergedBox
      ) {
        fillDownEndRowIndex = rowIndex - 1;
        return fillDownEndRowIndex;
      }
    }
  }
  return fillDownEndRowIndex;
}

/**
 * Computes the double-click fill-down box: the current selection expanded downward
 * through any following blank, non-merged rows. Returns the expanded selection box
 * with maxI replaced by the computed max row.
 * Returns null when there is no selection.
 */
export function computeDoubleClickFillDownBox(
  tableCells: Array<Array<Cell>>,
  computedTable: Array<Array<EvaluatedCell>>,
  expandedBox: MultiSelectionBox | null
): MultiSelectionBox | null {
  if (expandedBox == null) {
    return null;
  }
  return {
    ...expandedBox,
    maxI: computeMaxRowOnFillDown(tableCells, computedTable, expandedBox),
  };
}
